import { createContext, useContext, useEffect, useState } from "react";

type TTheme = "dark" | "light" | "system";

interface IThemeProviderProps {
    children: React.ReactNode;
    defaultTheme?: TTheme;
    storageKey?: string;
}

interface IThemeProviderContext {
    theme: TTheme;
    setTheme: (theme: TTheme) => void;
}

const ThemeProviderContext = createContext<IThemeProviderContext | undefined>(undefined);

export function ThemeProvider({
    children,
    defaultTheme = "system",
    storageKey = "smart-mail.theme",
    ...props
}: IThemeProviderProps) {
    const [theme, setTheme] = useState<TTheme>(
        () => (localStorage.getItem(storageKey) as TTheme) || defaultTheme,
    );

    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove("light", "dark");

        if (theme === "system") {
            const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
                ? "dark"
                : "light";
            root.classList.add(systemTheme);
        } else {
            root.classList.add(theme);
        }
    }, [theme]);

    const value: IThemeProviderContext = {
        theme,
        setTheme: (nextTheme: TTheme) => {
            localStorage.setItem(storageKey, nextTheme);
            setTheme(nextTheme);
        },
    };

    return (
        <ThemeProviderContext.Provider {...props} value={value}>
            {children}
        </ThemeProviderContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with provider; refactor TBD
export const useTheme = () => {
    const context = useContext(ThemeProviderContext);
    if (context === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};
